// AuthContext - Gerenciamento Global de Autenticação
import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updateEmail,
  verifyBeforeUpdateEmail
} from "firebase/auth";
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp, Timestamp } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

const AuthContext = createContext(null);

// Trial duration in days
const TRIAL_DURATION_DAYS = 7;

// Paid plans that bypass trial
const PAID_PLANS = ["annual", "semiannual", "monthly", "pix_31"];

// Error messages with Troquinho's personality
const AUTH_ERRORS = {
  "auth/email-already-in-use": "Esse e-mail já tá no nosso cofrinho! Se a conta for sua, tenta fazer login.",
  "auth/account-exists-with-different-credential": "Esse e-mail já tá sendo usado em outro lugar. Tenta o login normal!",
  "auth/invalid-email": "Esse e-mail tá meio estranho... Confere aí se não tem um espaço sobrando!",
  "auth/operation-not-allowed": "Opa, essa função tá desativada. O porquinho ficou de folga!",
  "auth/weak-password": "Senha muito magrinha! Coloca pelo menos 6 caracteres nela.",
  "auth/user-disabled": "Essa conta foi suspensa. O porquinho se comportou mal...",
  "auth/user-not-found": "E-mail ou senha não conferem. Dá uma conferida aí!",
  "auth/wrong-password": "E-mail ou senha não conferem. Dá uma conferida aí!",
  "auth/invalid-credential": "Credenciais inválidas. Tem certeza que é você mesmo?",
  "auth/too-many-requests": "Calma, apressadinho! Muitas tentativas. Espera um pouco.",
  "auth/network-request-failed": "Sua internet deu uma tropeçada. Tenta de novo!",
  "default": "O porquinho entalou num erro desconhecido. Tenta de novo em instantes!"
};

const getErrorMessage = (error) => {
  // Se receber o objeto de erro do Firebase, extrai o code. Se receber string, usa a string.
  const errorCode = typeof error === 'string' ? error : (error?.code || error?.message);
  
  // Se for um erro de senha incorreta ou usuário não encontrado, 
  // o Firebase unificou para 'invalid-credential' em versões novas por segurança.
  if (errorCode === "auth/invalid-credential") {
    return AUTH_ERRORS["auth/invalid-credential"];
  }
  
  return AUTH_ERRORS[errorCode] || AUTH_ERRORS["default"];
};


const calculateTrialStatus = (userProfile) => {
  if (!userProfile) return { isExpired: false, trialDaysLeft: 0, isPaidUser: false };
  
  const now = new Date();
  
  // --- LÓGICA DETETIVE PARA COMPATIBILIDADE (LEGADO) ---
  const isMonthly = userProfile.plan === 'monthly';
  // Se não tem status, mas é plano mensal, assumimos que está 'active'
  const inferredStatus = userProfile.planStatus || (isMonthly ? 'active' : 'free');
  // Se não tem a flag de recorrência, mas é mensal, assumimos que é 'true'
  const inferredRecurring = userProfile.isRecurring !== undefined ? userProfile.isRecurring : isMonthly;

  const premiumStatuses = ['active', 'cancelled_pending'];

  // PRIORIDADE 1: Verifica status pago definido pelo webhook
  if (premiumStatuses.includes(inferredStatus) && userProfile.expiresAt) {
    const expiresAt = userProfile.expiresAt instanceof Timestamp 
      ? userProfile.expiresAt.toDate() 
      : new Date(userProfile.expiresAt);
    
   if (expiresAt > now) {
      // Calculamos a diferença exata em dias
      const diffInMs = expiresAt.getTime() - now.getTime();
      const diffInDays = Math.max(0, Math.ceil(diffInMs / (1000 * 60 * 60 * 24)));
      
      return { 
        isExpired: false, 
        trialDaysLeft: null, 
        isPaidUser: true,
        planExpiresAt: expiresAt,
        daysUntilExpiry: diffInDays,
        inferredPlanStatus: inferredStatus,
        inferredIsRecurring: inferredRecurring
      };
    }
  }
  
  // PRIORIDADE 2: Verifica planos antigos (backward compatibility)
  const plan = userProfile.plan || "free";
  const isPaidUser = PAID_PLANS.includes(plan);
  
  if (isPaidUser && !userProfile.expiresAt) {
    return { 
      isExpired: false, 
      trialDaysLeft: null, 
      isPaidUser: true,
      inferredPlanStatus: inferredStatus,
      inferredIsRecurring: inferredRecurring
    };
  }
  
  // PRIORIDADE 3: Verifica trial (usuário free)
  const trialEndsAt = userProfile.trialEndsAt;
  if (!trialEndsAt) {
    return { isExpired: false, trialDaysLeft: TRIAL_DURATION_DAYS, isPaidUser: false, inferredPlanStatus: 'free', inferredIsRecurring: false };
  }
  
  const trialEnd = trialEndsAt instanceof Timestamp ? trialEndsAt.toDate() : new Date(trialEndsAt);
  const diffTime = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return {
    isExpired: diffDays <= 0,
    trialDaysLeft: Math.max(0, diffDays),
    isPaidUser: false,
    inferredPlanStatus: inferredStatus,
    inferredIsRecurring: inferredRecurring
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0); // ✨ NOSSO GATILHO SALVADOR DE VIDAS

  // Listen to auth state + real-time user profile (libera UI assim que webhook altera Firestore)
  useEffect(() => {
    let userProfileUnsubscribe = null;

    const authUnsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setLoading(true);

      // Limpa listener anterior do user profile se existir (evita vazamento entre trocas de conta)
      if (userProfileUnsubscribe) {
        userProfileUnsubscribe();
        userProfileUnsubscribe = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        // Real-time listener: atualiza userProfile sempre que o doc no Firestore mudar
        userProfileUnsubscribe = onSnapshot(
          doc(db, "users", firebaseUser.uid),
          (snapshot) => {
            if (snapshot.exists()) {
              setUserProfile(snapshot.data());
            }
            setLoading(false);
          },
          (err) => {
            console.error("Error listening to user profile:", err);
            setLoading(false);
          }
        );
      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      if (userProfileUnsubscribe) userProfileUnsubscribe();
      authUnsubscribe();
    };
  }, []);

  // Register new user
  const register = async (name, email, password) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update display name
      await updateProfile(firebaseUser, { displayName: name });

      // Create user profile in Firestore
      const userProfileData = {
        uid: firebaseUser.uid,
        name: name,
        email: email,
        createdAt: serverTimestamp(),
        onboardingCompleted: false,
        plan: "free",
        settings: {
          notifications: true,
          currency: "BRL",
          language: "pt-BR"
        }
      };

      await setDoc(doc(db, "users", firebaseUser.uid), userProfileData);
      setUserProfile(userProfileData);

      // Send email verification
      await sendEmailVerification(firebaseUser);

      return { success: true, user: firebaseUser };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Login
  const login = async (email, password) => {
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Resend email verification
  const resendVerificationEmail = async () => {
    setError(null);
    try {
      if (user) {
        await sendEmailVerification(user);
        return { success: true };
      }
      return { success: false, error: "Nenhum usuário logado" };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

// Reload user to check emailVerified status
  const reloadUser = async () => {
    setError(null);
    try {
      if (auth.currentUser) {
        // 1. Puxa as infos mais recentes do Firebase
        await auth.currentUser.reload();
        
        // 2. FORÇA a limpeza de cache do token (Isso resolve o bug de falso-negativo)
        await auth.currentUser.getIdToken(true);
        
        // 3. Puxamos o gatilho. Como o state muda, o Context avisa o app todo que algo atualizou!
        setReloadTrigger(prev => prev + 1);
        
        return { 
          success: true, 
          emailVerified: auth.currentUser.emailVerified, // Pega o dado fresquinho
          user: auth.currentUser 
        };
      }
      return { success: false, error: "Nenhum usuário logado" };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Update user email (requires recent login)
  const changeUserEmail = async (newEmail) => {
    setError(null);
    try {
      if (!user) throw new Error("Nenhum usuário logado");
      
      // Use verifyBeforeUpdateEmail to send verification to new email
      await verifyBeforeUpdateEmail(user, newEmail);
      
      // Update email in Firestore as well
      await setDoc(doc(db, "users", user.uid), { email: newEmail }, { merge: true });
      setUserProfile(prev => ({ ...prev, email: newEmail }));
      
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code);
      setError(message);
      return { success: false, error: message };
    }
  };

  // Update user profile in Firestore
  const updateUserProfile = async (data) => {
    setError(null);
    try {
      if (!user) throw new Error("Nenhum usuário logado");
      
      await setDoc(doc(db, "users", user.uid), data, { merge: true });
      setUserProfile(prev => ({ ...prev, ...data }));
      
      // Update display name if provided
      if (data.name) {
        await updateProfile(user, { displayName: data.name });
      }
      
      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err.code); // <-- Agora ele usa a lista de erros correta
      setError(message);
      return { success: false, error: message };
    }
  };

  // Complete onboarding - starts the 7-day trial
  const completeOnboarding = async (onboardingData) => {
    // Calculate trial end date (7 days from now)
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DURATION_DAYS);
    
    return updateUserProfile({
      onboardingCompleted: true,
      onboardingData: onboardingData,
      trialEndsAt: Timestamp.fromDate(trialEndDate),
      trialStartedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  // Calculate trial status
  const trialStatus = useMemo(() => {
    return calculateTrialStatus(userProfile);
  }, [userProfile]);

  const value = {
    user,
    userProfile,
    loading,
    error,
    register,
    login,
    logout,
    resetPassword,
    resendVerificationEmail,
    reloadUser,
    changeUserEmail,
    updateUserProfile,
    completeOnboarding,
    isAuthenticated: !!user,
    isEmailVerified: user?.emailVerified || false, // Usa o 'user' do estado que é atualizado no reload
    hasCompletedOnboarding: !!userProfile?.onboardingCompleted,
    _trigger: reloadTrigger, // ✨ Garante que o Context atualize
    // Trial & Subscription status
    isExpired: trialStatus.isExpired,
    trialDaysLeft: trialStatus.trialDaysLeft,
    isPaidUser: trialStatus.isPaidUser,
    planExpiresAt: trialStatus.planExpiresAt,
    currentPlan: userProfile?.plan || "free",
    planStatus: trialStatus.inferredPlanStatus, // Usa o status deduzido pela função
    isRecurring: trialStatus.inferredIsRecurring, // Usa a recorrência deduzida
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}

export default AuthContext;
